import identity from "lodash/identity";
import type { AnyAction } from "redux";

import {
    setIsHsCompaniesExportEnabled,
    setIsHsContactsExportEnabled,
} from "app/pages/sales-intelligence/sub-modules/management/store/actions/hubspotActions";
import {
    setIsDynamicsAccountsExportEnabled,
    setIsDynamicsContactsExportEnabled,
    setIsDynamicsLeadsExportEnabled,
} from "app/pages/sales-intelligence/sub-modules/management/store/actions/dynamicsActions";
import {
    setIsSfAccountsExportEnabled,
    setIsSfContactsExportEnabled,
    setIsSfLeadsExportEnabled,
} from "app/pages/sales-intelligence/sub-modules/management/store/actions/salesforceActions";
import {
    putHsCompaniesToggleThunk,
    putHsContactsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/toggles";
import {
    putDynamicsAccountsToggleThunk,
    putDynamicsCompanyAsLeadsToggleThunk,
    putDynamicsContactsToggleThunk,
    putDynamicsLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/toggles";
import {
    putSfAccountsToggleThunk,
    putSfCompanyAsLeadsToggleThunk,
    putSfContactsToggleThunk,
    putSfLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    selectHsAccountsMissingRequiredFieldsCount,
    selectHsContactsMissingRequiredFieldsCount,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import {
    selectHubspotAccountsToggleState,
    selectHubspotCompaniesCount,
    selectHubspotContactsCount,
    selectHubspotContactsToggleState,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectDynamicsAccountsMissingRequiredFieldsCount,
    selectDynamicsCompanyAsLeadsMissingRequiredFieldsCount,
    selectDynamicsContactsMissingRequiredFieldsCount,
    selectDynamicsLeadsMissingRequiredFieldsCount,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamics/exportSelectors";
import {
    selectSfAccountsMissingRequiredFieldsCount,
    selectSfCompanyAsLeadsMissingRequiredFieldsCount,
    selectSfContactsMissingRequiredFieldsCount,
    selectSfLeadsMissingRequiredFieldsCount,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import {
    selectDynamicsAccountsCount,
    selectDynamicsAccountsToggleState,
    selectDynamicsCompanyAsLeadsToggleState,
    selectDynamicsContactsCount,
    selectDynamicsContactsToggleState,
    selectDynamicsLeadsCount,
    selectDynamicsLeadsToggleState,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import {
    selectSalesforceAccountsCount,
    selectSalesforceAccountsToggleState,
    selectSalesforceCompanyAsLeadsToggleState,
    selectSalesforceContactsCount,
    selectSalesforceContactsToggleState,
    selectSalesforceLeadsCount,
    selectSalesforceLeadsToggleState,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import type { ExportName, HubspotExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { RootState, ThunkActionCommon } from "single-spa/store/types";

import { getAccountsFields } from "../../configs/accountsFields";
import { getContactsFields } from "../../configs/contactsFields";
import { getLeadsFields } from "../../configs/leadsFields";

export type ToggleProps = {
    text: string;
    totalFields: number;
    onToggle: (payload: boolean) => ThunkActionCommon;
    selectValue: (state: RootState) => boolean;
    selectMappedFieldsCount: (state: RootState) => number;
    selectMissingRequiredFieldsCount: (state: RootState) => number;
    setLocalValue: (payload: boolean) => AnyAction;
};

const isHubspotExportName = (name: ExportName): name is HubspotExportName => {
    return name === "contacts" || name === "accounts";
};

export const getToggleProps = (integrationName: Integration, exportName: ExportName): ToggleProps => {
    const contactsFieldsCount = Object.keys(getContactsFields(integrationName)).length;
    const accountsFieldsCount = Object.keys(getAccountsFields()).length;

    if (integrationName === "hubspot" && isHubspotExportName(exportName)) {
        const config: Record<HubspotExportName, ToggleProps> = {
            contacts: {
                onToggle: putHsContactsToggleThunk,
                selectValue: selectHubspotContactsToggleState,
                selectMappedFieldsCount: selectHubspotContactsCount,
                text: "si.management.integration.export.preferences.toggle.contacts",
                totalFields: contactsFieldsCount,
                selectMissingRequiredFieldsCount: selectHsContactsMissingRequiredFieldsCount,
                setLocalValue: setIsHsContactsExportEnabled,
            },
            accounts: {
                onToggle: putHsCompaniesToggleThunk,
                selectValue: selectHubspotAccountsToggleState,
                selectMappedFieldsCount: selectHubspotCompaniesCount,
                text: "si.management.integration.export.preferences.toggle.companies",
                totalFields: accountsFieldsCount,
                selectMissingRequiredFieldsCount: selectHsAccountsMissingRequiredFieldsCount,
                setLocalValue: setIsHsCompaniesExportEnabled,
            },
        };

        return config[exportName];
    }

    if (integrationName === "salesforce") {
        const leadsFieldsCount = new Set(Object.keys(getLeadsFields())).size;

        const config: Record<ExportName, ToggleProps> = {
            leads: {
                onToggle: putSfLeadsToggleThunk,
                selectValue: selectSalesforceLeadsToggleState,
                selectMappedFieldsCount: selectSalesforceLeadsCount,
                text: "si.management.integration.export.preferences.toggle.leads",
                totalFields: leadsFieldsCount,
                selectMissingRequiredFieldsCount: selectSfLeadsMissingRequiredFieldsCount,
                setLocalValue: setIsSfLeadsExportEnabled,
            },
            companyAsLeads: {
                onToggle: putSfCompanyAsLeadsToggleThunk,
                selectValue: selectSalesforceCompanyAsLeadsToggleState,
                selectMappedFieldsCount: selectSalesforceLeadsCount,
                text: "si.management.integration.export.preferences.toggle.leads",
                totalFields: accountsFieldsCount,
                selectMissingRequiredFieldsCount: selectSfCompanyAsLeadsMissingRequiredFieldsCount,
                setLocalValue: setIsSfLeadsExportEnabled,
            },
            contacts: {
                onToggle: putSfContactsToggleThunk,
                selectValue: selectSalesforceContactsToggleState,
                selectMappedFieldsCount: selectSalesforceContactsCount,
                text: "si.management.integration.export.preferences.toggle.contacts",
                totalFields: contactsFieldsCount,
                selectMissingRequiredFieldsCount: selectSfContactsMissingRequiredFieldsCount,
                setLocalValue: setIsSfContactsExportEnabled,
            },
            accounts: {
                onToggle: putSfAccountsToggleThunk,
                selectValue: selectSalesforceAccountsToggleState,
                selectMappedFieldsCount: selectSalesforceAccountsCount,
                text: "si.management.integration.export.preferences.toggle.accounts",
                totalFields: accountsFieldsCount,
                selectMissingRequiredFieldsCount: selectSfAccountsMissingRequiredFieldsCount,
                setLocalValue: setIsSfAccountsExportEnabled,
            },
        };

        return config[exportName];
    }

    if (integrationName === "dynamics") {
        const leadsFieldsCount = new Set(Object.keys(getLeadsFields())).size;

        const config: Record<ExportName, ToggleProps> = {
            leads: {
                onToggle: putDynamicsLeadsToggleThunk,
                selectValue: selectDynamicsLeadsToggleState,
                selectMappedFieldsCount: selectDynamicsLeadsCount,
                text: "si.management.integration.export.preferences.toggle.leads",
                totalFields: leadsFieldsCount,
                selectMissingRequiredFieldsCount: selectDynamicsLeadsMissingRequiredFieldsCount,
                setLocalValue: setIsDynamicsLeadsExportEnabled,
            },
            companyAsLeads: {
                onToggle: putDynamicsCompanyAsLeadsToggleThunk,
                selectValue: selectDynamicsCompanyAsLeadsToggleState,
                selectMappedFieldsCount: selectDynamicsLeadsCount,
                text: "si.management.integration.export.preferences.toggle.leads",
                totalFields: accountsFieldsCount,
                selectMissingRequiredFieldsCount: selectDynamicsCompanyAsLeadsMissingRequiredFieldsCount,
                setLocalValue: setIsDynamicsLeadsExportEnabled,
            },
            contacts: {
                onToggle: putDynamicsContactsToggleThunk,
                selectValue: selectDynamicsContactsToggleState,
                selectMappedFieldsCount: selectDynamicsContactsCount,
                text: "si.management.integration.export.preferences.toggle.contacts",
                totalFields: contactsFieldsCount,
                selectMissingRequiredFieldsCount: selectDynamicsContactsMissingRequiredFieldsCount,
                setLocalValue: setIsDynamicsContactsExportEnabled,
            },
            accounts: {
                onToggle: putDynamicsAccountsToggleThunk,
                selectValue: selectDynamicsAccountsToggleState,
                selectMappedFieldsCount: selectDynamicsAccountsCount,
                text: "si.management.integration.export.preferences.toggle.accounts",
                totalFields: accountsFieldsCount,
                selectMissingRequiredFieldsCount: selectDynamicsAccountsMissingRequiredFieldsCount,
                setLocalValue: setIsDynamicsAccountsExportEnabled,
            },
        };

        return config[exportName];
    }

    console.error(`[getToggleProps] Unknown integration: "${String(integrationName)}`);

    return {
        text: "",
        totalFields: 0,
        onToggle: identity,
        selectValue: identity,
        selectMappedFieldsCount: identity,
        selectMissingRequiredFieldsCount: identity,
        setLocalValue: identity,
    };
};
